export type DealerType = 'dealer' | 'dealer_group' | 'oem' | 'oem_group' | 'franchise_dealer';

export const DEALER_TYPE_LABELS: Record<DealerType, string> = {
    dealer: 'Dealer',
    dealer_group: 'Dealer Group',
    oem: 'OEM',
    oem_group: 'OEM Group',
    franchise_dealer: 'Franchise Dealer',
};

export const DEALER_TYPE_OPTIONS: { value: DealerType; label: string }[] = [
    { value: 'dealer', label: 'Dealer' },
    { value: 'dealer_group', label: 'Dealer Group' },
    { value: 'oem', label: 'OEM' },
    { value: 'oem_group', label: 'OEM Group' },
    { value: 'franchise_dealer', label: 'Franchise Dealer' },
];

export interface DealerSummary {
    id: string;
    name: string;
}

export interface DealersByIdsResponse {
    success: boolean;
    data: DealerSummary[];
}

export interface DealerStatus {
    id: string;
    key: string;
    label: string;
    sort_order: number;
}

export interface Dealer {
    id: string;
    dealer_code: string;
    name: string;
    trading_name?: string | null;
    email?: string | null;
    phone?: string | null;
    website?: string | null;
    franchise_code?: string | null;
    status_id?: string;
    status_key?: string;
    status?: DealerStatus;
    deactivated_at?: string | null;
    type?: DealerType;
    is_group?: boolean;
    parent_dealer_id?: string | null;
    child_dealers_count?: number;
    address_line_1: string;
    address_line_2: string | null;
    city: string;
    postcode: string;
    latitude: string | number;
    longitude: string | number;
    created_at?: string;
    updated_at?: string;
}

export interface DealerBankAccount {
    id: string;
    dealer_id: string;
    account_name: string;
    account_number_last4: string;
    sort_code_last2: string;
    bank_name: string | null;
    is_primary: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface DealerContact {
    id: string;
    dealer_id: string;
    name: string;
    role: string | null;
    email: string | null;
    phone: string | null;
    notify_agreements: boolean;
    notify_payments: boolean;
    created_at?: string;
    updated_at?: string;
    deleted_at?: string | null;
}

export interface DealerMargin {
    id: string;
    dealer_id: string;
    product_type: string;
    margin_bps: number;
    effective_from: string;
    effective_to: string | null;
    created_at?: string;
    updated_at?: string;
}

export interface DealerSearchParams {
    search?: string;
    page?: number;
    per_page?: number;
    is_group?: boolean;
    /** Live = not retired (status != inactive). Used by selection pickers that need
     * any non-retired dealer, distinct from the exact per-status `status` filter. */
    is_live?: boolean;
    type?: DealerType;
    status?: string;
    sort_by?: string;
    sort_dir?: 'asc' | 'desc';
    /**
     * Narrowing filter, composes AND with the server's tenant scope. Omit for
     * "no restriction" -- never send an empty array.
     */
    dealer_ids?: string[];
    /** Narrows to dealers the caller can sell this product for. */
    selling_product_id?: string;
    /** Quote journey only: dealers the caller can quote and sell at through a group holding a product. */
    sellable_only?: boolean;
    /** Sales dealer picker only: binds admin and internal users to their security groups. */
    context?: 'sales';
}

export interface PaginatedMeta {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from?: number;
    to?: number;
}

export interface DealerSearchResponse {
    success: boolean;
    data: Dealer[];
    meta: PaginatedMeta;
}

// A franchisor/franchisee row IS a Dealer, so the franchise-axis list
// endpoints (GET .../franchises, GET .../franchisees) share the same
// paginated envelope shape as DealerSearchResponse.
export type FranchiseListResponse = DealerSearchResponse;

export interface ApiListResponse<T> {
    success: boolean;
    data: T[];
}

export interface ApiItemResponse<T> {
    success: boolean;
    data: T;
    message?: string;
}

export interface CreateDealerPayload {
    dealer_code?: string;
    name: string;
    trading_name?: string;
    email?: string;
    phone?: string;
    website?: string;
    franchise_code?: string;
    status?: string;
    type?: DealerType;
    parent_dealer_id?: string;
    address_line_1: string;
    address_line_2?: string;
    city: string;
    postcode: string;
    latitude?: number;
    longitude?: number;
    create_security_group?: boolean;
}

export type UpdateDealerPayload = Partial<Omit<CreateDealerPayload, 'create_security_group'>>;

export interface CreateDealerBankAccountPayload {
    account_name: string;
    account_number: string;
    sort_code: string;
    bank_name?: string;
    is_primary?: boolean;
}

export type UpdateDealerBankAccountPayload = Partial<CreateDealerBankAccountPayload>;

export interface CreateDealerContactPayload {
    name: string;
    role?: string;
    email?: string;
    phone?: string;
    notify_agreements?: boolean;
    notify_payments?: boolean;
}

export type UpdateDealerContactPayload = Partial<CreateDealerContactPayload>;

export interface CreateDealerMarginPayload {
    product_type: string;
    margin_bps: number;
    effective_from?: string;
    effective_to?: string;
}

export type UpdateDealerMarginPayload = Partial<CreateDealerMarginPayload>;
